import { Injectable } from '@angular/core';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

@Injectable({ providedIn: 'root' })
export class PublicFormCaptchaService {
  private loadPromise: Promise<void> | null = null;

  loadTurnstile(): Promise<void> {
    if (window.turnstile) {
      return Promise.resolve();
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector('script[data-ain-turnstile]');
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('turnstile_load_failed')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset['ainTurnstile'] = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('turnstile_load_failed'));
      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  renderTurnstile(
    container: HTMLElement,
    siteKey: string,
    callbacks: { onToken: (token: string) => void; onExpired?: () => void; onError?: () => void }
  ): Promise<string> {
    return this.loadTurnstile().then(() => {
      if (!window.turnstile) {
        throw new Error('turnstile_unavailable');
      }

      return window.turnstile.render(container, {
        sitekey: siteKey,
        callback: callbacks.onToken,
        'expired-callback': callbacks.onExpired,
        'error-callback': callbacks.onError
      });
    });
  }

  resetTurnstile(widgetId?: string): void {
    window.turnstile?.reset(widgetId);
  }

  removeTurnstile(widgetId: string): void {
    window.turnstile?.remove(widgetId);
  }
}
